// ── Tournament Registry ──
// Central registry of all available tournaments

import type { TournamentConfig, TournamentData } from "./types";

// Bundled tournament logos — self-hosted (was Wikipedia hotlinks that got
// rate-limited to 429 in production). Vite fingerprints and serves them
// from /assets.
import logoWorldCup from "@/assets/tournament-logos/world-cup-2026.png";
import logoConcacaf from "@/assets/tournament-logos/concacaf-champions-cup-2026.png";
import logoUcl      from "@/assets/tournament-logos/ucl-2026.png";
import logoUel      from "@/assets/tournament-logos/uel-2026.png";
import logoUecl     from "@/assets/tournament-logos/uecl-2026.png";
import logoLeagues  from "@/assets/tournament-logos/leagues-cup-2026.png";
import logoMls      from "@/assets/tournament-logos/mls-cup-2026.png";
import logoLiberta  from "@/assets/tournament-logos/copa-libertadores-2026.png";
import logoUsOpen   from "@/assets/tournament-logos/us-open-cup-2026.png";

// Re-export types
export type { TournamentConfig, TournamentData, TournamentTeam, TournamentRound, KnockoutMatch, TeamSource, CompletedMatchResult } from "./types";

// Lazy imports for tournament data (code-split per tournament)
const tournamentModules: Record<string, () => Promise<{ default: TournamentData }>> = {
  "world-cup-2026": () => import("./world-cup-2026"),
  "concacaf-champions-cup-2026": () => import("./concacaf-champions-cup-2026"),
  "ucl-2026": () => import("./ucl-2026"),
  "uel-2026": () => import("./uel-2026"),
  "uecl-2026": () => import("./uecl-2026"),
  "leagues-cup-2026": () => import("./leagues-cup-2026"),
  "mls-cup-2026": () => import("./mls-cup-2026"),
  "copa-libertadores-2026": () => import("./copa-libertadores-2026"),
  "us-open-cup-2026": () => import("./us-open-cup-2026"),
};

// ── Lightweight config-only registry (no team data, loads instantly) ──
export const TOURNAMENTS: TournamentConfig[] = [
  {
    id: "world-cup-2026",
    slug: "world-cup-2026",
    name: "2026 FIFA World Cup",
    shortName: "World Cup 2026",
    emoji: "🏆",
    format: "group-knockout",
    teamType: "national",
    teamCount: 48,
    groupCount: 12,
    groupSize: 4,
    hasThirdPlaceAdvancement: true,
    hasTiebreaker: true,
    hasGoldenBoot: true,
    rounds: [
      { id: "r32", name: "Round of 32", matchCount: 16, roundIndex: 0 },
      { id: "r16", name: "Round of 16", matchCount: 8, roundIndex: 1 },
      { id: "qf", name: "Quarterfinals", matchCount: 4, roundIndex: 2 },
      { id: "sf", name: "Semifinals", matchCount: 2, roundIndex: 3 },
      { id: "final", name: "Final", matchCount: 1, roundIndex: 4 },
    ],
    knockoutStartRound: "r32",
    startDate: "2026-06-11",
    lockDeadline: "2026-06-11",
    endDate: "2026-07-19",
    status: "upcoming",
    confederation: "FIFA",
    location: "USA, Canada & Mexico",
    accentColor: "primary",
    prizeDescription: "$5,000 in Prizes",
    logoUrl: logoWorldCup,
    isActive: true,
    isFeatured: true,
  },
  {
    id: "concacaf-champions-cup-2026",
    slug: "concacaf-champions-cup-2026",
    name: "CONCACAF Champions Cup 2026",
    shortName: "Champions Cup",
    emoji: "🏆",
    format: "single-elimination",
    teamType: "club",
    teamCount: 16,
    rounds: [
      { id: "r16", name: "Round of 16", matchCount: 8, roundIndex: 0 },
      { id: "qf", name: "Quarterfinals", matchCount: 4, roundIndex: 1 },
      { id: "sf", name: "Semifinals", matchCount: 2, roundIndex: 2 },
      { id: "final", name: "Final", matchCount: 1, roundIndex: 3 },
    ],
    knockoutStartRound: "r16",
    startDate: "2026-02-01",
    lockDeadline: "2026-02-01",
    endDate: "2026-05-30",
    status: "active",
    confederation: "CONCACAF",
    accentColor: "emerald",
    prizeDescription: "$1,000 in Prizes",
    logoUrl: logoConcacaf,
    isActive: true,
  },
  {
    id: "ucl-2026",
    slug: "ucl-2026",
    name: "UEFA Champions League 2025-26",
    shortName: "Champions League",
    emoji: "⭐",
    format: "single-elimination",
    teamType: "club",
    teamCount: 16,
    rounds: [
      { id: "r16", name: "Round of 16", matchCount: 8, roundIndex: 0 },
      { id: "qf", name: "Quarterfinals", matchCount: 4, roundIndex: 1 },
      { id: "sf", name: "Semifinals", matchCount: 2, roundIndex: 2 },
      { id: "final", name: "Final", matchCount: 1, roundIndex: 3 },
    ],
    knockoutStartRound: "r16",
    startDate: "2026-02-01",
    lockDeadline: "2026-02-01",
    endDate: "2026-05-30",
    status: "active",
    confederation: "UEFA",
    location: "Budapest",
    accentColor: "blue",
    prizeDescription: "$5,000 in Prizes",
    logoUrl: logoUcl,
    isActive: true,
    isFeatured: true,
  },
  {
    id: "uel-2026",
    slug: "uel-2026",
    name: "UEFA Europa League 2025-26",
    shortName: "Europa League",
    emoji: "🟠",
    format: "single-elimination",
    teamType: "club",
    teamCount: 16,
    rounds: [
      { id: "r16", name: "Round of 16", matchCount: 8, roundIndex: 0 },
      { id: "qf", name: "Quarterfinals", matchCount: 4, roundIndex: 1 },
      { id: "sf", name: "Semifinals", matchCount: 2, roundIndex: 2 },
      { id: "final", name: "Final", matchCount: 1, roundIndex: 3 },
    ],
    knockoutStartRound: "r16",
    startDate: "2026-02-01",
    lockDeadline: "2026-02-01",
    endDate: "2026-05-27",
    status: "active",
    confederation: "UEFA",
    location: "Leipzig",
    accentColor: "orange",
    prizeDescription: "$1,000 in Prizes",
    logoUrl: logoUel,
    isActive: true,
  },
  {
    id: "uecl-2026",
    slug: "uecl-2026",
    name: "UEFA Conference League 2025-26",
    shortName: "Conference League",
    emoji: "🟢",
    format: "single-elimination",
    teamType: "club",
    teamCount: 16,
    rounds: [
      { id: "r16", name: "Round of 16", matchCount: 8, roundIndex: 0 },
      { id: "qf", name: "Quarterfinals", matchCount: 4, roundIndex: 1 },
      { id: "sf", name: "Semifinals", matchCount: 2, roundIndex: 2 },
      { id: "final", name: "Final", matchCount: 1, roundIndex: 3 },
    ],
    knockoutStartRound: "r16",
    startDate: "2026-02-01",
    lockDeadline: "2026-02-01",
    endDate: "2026-05-27",
    status: "active",
    confederation: "UEFA",
    location: "Leipzig",
    accentColor: "green",
    prizeDescription: "$1,000 in Prizes",
    logoUrl: logoUecl,
    isActive: true,
  },
  {
    id: "leagues-cup-2026",
    slug: "leagues-cup-2026",
    name: "Leagues Cup 2026",
    shortName: "Leagues Cup",
    emoji: "🏆",
    format: "single-elimination",
    teamType: "club",
    teamCount: 32,
    rounds: [
      { id: "r32", name: "Round of 32", matchCount: 16, roundIndex: 0 },
      { id: "r16", name: "Round of 16", matchCount: 8, roundIndex: 1 },
      { id: "qf", name: "Quarterfinals", matchCount: 4, roundIndex: 2 },
      { id: "sf", name: "Semifinals", matchCount: 2, roundIndex: 3 },
      { id: "final", name: "Final", matchCount: 1, roundIndex: 4 },
    ],
    knockoutStartRound: "r32",
    startDate: "2026-08-04",
    lockDeadline: "2026-08-04",
    endDate: "2026-09-06",
    status: "upcoming",
    confederation: "CONCACAF",
    accentColor: "violet",
    prizeDescription: "$1,000 in Prizes",
    logoUrl: logoLeagues,
    isActive: true,
  },
  {
    id: "mls-cup-2026",
    slug: "mls-cup-2026",
    name: "MLS Cup Playoffs 2026",
    shortName: "MLS Cup",
    emoji: "⚽",
    format: "single-elimination",
    teamType: "club",
    teamCount: 16,
    rounds: [
      { id: "wc", name: "Wildcard", matchCount: 4, roundIndex: 0 },
      { id: "r1", name: "Round One", matchCount: 8, roundIndex: 1 },
      { id: "csf", name: "Conference Semis", matchCount: 4, roundIndex: 2 },
      { id: "cf", name: "Conference Finals", matchCount: 2, roundIndex: 3 },
      { id: "final", name: "MLS Cup Final", matchCount: 1, roundIndex: 4 },
    ],
    knockoutStartRound: "wc",
    startDate: "2026-11-18",
    lockDeadline: "2026-11-18",
    endDate: "2026-12-18",
    status: "upcoming",
    confederation: "MLS",
    accentColor: "indigo",
    prizeDescription: "$1,000 in Prizes",
    logoUrl: logoMls,
    isActive: true,
  },
  {
    id: "copa-libertadores-2026",
    slug: "copa-libertadores-2026",
    name: "Copa Libertadores 2026",
    shortName: "Libertadores",
    emoji: "🏆",
    format: "single-elimination",
    teamType: "club",
    teamCount: 16,
    rounds: [
      { id: "r16", name: "Round of 16", matchCount: 8, roundIndex: 0 },
      { id: "qf", name: "Quarterfinals", matchCount: 4, roundIndex: 1 },
      { id: "sf", name: "Semifinals", matchCount: 2, roundIndex: 2 },
      { id: "final", name: "Final", matchCount: 1, roundIndex: 3 },
    ],
    knockoutStartRound: "r16",
    startDate: "2026-06-01",
    lockDeadline: "2026-06-01",
    endDate: "2026-11-28",
    status: "upcoming",
    confederation: "CONMEBOL",
    location: "Montevideo",
    accentColor: "yellow",
    prizeDescription: "$1,000 in Prizes",
    logoUrl: logoLiberta,
    isActive: true,
  },
  {
    id: "us-open-cup-2026",
    slug: "us-open-cup-2026",
    name: "US Open Cup 2026",
    shortName: "US Open Cup",
    emoji: "🇺🇸",
    format: "single-elimination",
    teamType: "club",
    teamCount: 32,
    rounds: [
      { id: "r32", name: "Round of 32", matchCount: 16, roundIndex: 0 },
      { id: "r16", name: "Round of 16", matchCount: 8, roundIndex: 1 },
      { id: "qf", name: "Quarterfinals", matchCount: 4, roundIndex: 2 },
      { id: "sf", name: "Semifinals", matchCount: 2, roundIndex: 3 },
      { id: "final", name: "Final", matchCount: 1, roundIndex: 4 },
    ],
    knockoutStartRound: "r32",
    startDate: "2026-03-17",
    lockDeadline: "2026-03-17",
    endDate: "2026-09-15",
    status: "active",
    confederation: "MLS",
    accentColor: "red",
    prizeDescription: "$1,000 in Prizes",
    logoUrl: logoUsOpen,
    isActive: true,
  },
];

// ── Helpers ──

/** Get tournament config by slug */
export function getTournamentBySlug(slug: string): TournamentConfig | undefined {
  return TOURNAMENTS.find(t => t.slug === slug);
}

/** Get tournament config by id */
export function getTournamentById(id: string): TournamentConfig | undefined {
  return TOURNAMENTS.find(t => t.id === id);
}

/** Load full tournament data (teams, matches, etc.) — code-split */
export async function loadTournamentData(slug: string): Promise<TournamentData | null> {
  const loader = tournamentModules[slug];
  if (!loader) return null;
  try {
    const mod = await loader();
    return mod.default;
  } catch (e) {
    console.error(`Failed to load tournament data for ${slug}:`, e);
    return null;
  }
}

/** Get all active tournaments */
export function getActiveTournaments(): TournamentConfig[] {
  return TOURNAMENTS.filter(t => t.isActive);
}

/** Get featured tournaments (for homepage) */
export function getFeaturedTournaments(): TournamentConfig[] {
  return TOURNAMENTS.filter(t => t.isFeatured);
}

/** Get tournaments by status */
export function getTournamentsByStatus(status: string): TournamentConfig[] {
  return TOURNAMENTS.filter(t => t.status === status);
}

/** Get tournaments by confederation */
export function getTournamentsByConfederation(confederation: string): TournamentConfig[] {
  return TOURNAMENTS.filter(t => t.confederation === confederation);
}
